import NativeAuthUrlListener from 'platform/NativeAuthUrlListener';
import { isNative } from 'platform/native';
import { useContext, useEffect, useMemo, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import router from 'routes';

import Locales from 'ui-component/Locales';
import NavigationScroll from 'layout/NavigationScroll';
import RTLLayout from 'ui-component/RTLLayout';
import Snackbar from 'ui-component/extended/Snackbar';
import Notistack from 'ui-component/third-party/Notistack';
import ThemeCustomization from 'themes';
import DatePickersLocaleProvider from 'ui-component/DatePickersLocaleProvider';
import { LocalizationContext, LocalizationLanguageProvider } from 'contexts/LocalizationContext';
import FaviconWithBadge from 'layout/FaviconWithBadge';
import { SignalRProvider } from 'contexts/SignalRContext';
import { SystemGlobalBannerProvider } from 'contexts/SystemGlobalBannerContext';
import { DiagnosticProvider } from 'features/SupportMode';

import { ClerkProvider, useClerk } from '@clerk/clerk-react';
import { AuthProvider } from 'contexts/AuthContext';
import ClerkBootstrap from 'utils/auth/ClerkBootstrap';
import Loader from 'ui-component/Loader';
import { Box, Button, Typography } from '@mui/material';
import { buildClerkLocalization } from 'utils/clerk-localization';

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY');

/**
 * Renders the app once Clerk has loaded for the first time.
 * Unlike <ClerkLoaded>, this component does NOT unmount the tree
 * if Clerk briefly returns to a loading state during token refresh.
 */
function ClerkGatedApp() {
  const clerk = useClerk();
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (clerk.loaded) {
      setReady(true);
      setFailed(false);
    }
  }, [clerk.loaded]);

  useEffect(() => {
    if (ready) return;
    const timeout = setTimeout(() => {
      if (!ready) setFailed(true);
    }, 15_000);
    return () => clearTimeout(timeout);
  }, [ready]);

  console.log('[DEBUG-REFRESH] ClerkGatedApp render — ready:', ready, new Date().toISOString());

  if (failed && !ready) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1302,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          bgcolor: 'background.default',
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" component="h1">
          שגיאה בטעינת ההתחברות
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          לא ניתן לטעון את שירות Clerk (רשת / חסימה / timeout). נסה לרענן את הדף או לבדוק חיבור לאינטרנט.
        </Typography>
        <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
          רענון הדף
        </Button>
      </Box>
    );
  }

  if (!ready) return <Loader />;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <ClerkBootstrap />
      <NativeAuthUrlListener />
      <AuthProvider>
        <ThemeCustomization>
          <RTLLayout>
            <Locales>
              <SystemGlobalBannerProvider>
                <DatePickersLocaleProvider>
                  <NavigationScroll>
                    <Notistack>
                      <DiagnosticProvider>
                        <SignalRProvider>
                          <Box
                            sx={{
                              flex: 1,
                              minHeight: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%',
                              width: '100%',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                              }}
                            >
                              <RouterProvider router={router} />
                            </Box>
                            <Snackbar />
                            {!isNative && <FaviconWithBadge />}
                          </Box>
                        </SignalRProvider>
                      </DiagnosticProvider>
                    </Notistack>
                  </NavigationScroll>
                </DatePickersLocaleProvider>
              </SystemGlobalBannerProvider>
            </Locales>
          </RTLLayout>
        </ThemeCustomization>
      </AuthProvider>
    </Box>
  );
}

/**
 * ClerkProvider צריך `localization` דינמי לפי שפת האפליקציה — לכן נשב על `LocalizationContext` כאן.
 */
function ClerkAppShell() {
  console.log('[DEBUG-REFRESH] App component mounting — if you see this repeatedly, it is a full remount', new Date().toISOString());
  const { currentLanguage } = useContext(LocalizationContext);
  const localization = useMemo(() => buildClerkLocalization(currentLanguage), [currentLanguage]);

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      localization={localization}
      // בלי היעדים האלה Clerk נופל ל"כתובת הבית" של ה-instance (funner.co.il —
      // האתר השיווקי): משתמש שסיים הרשמה/אימות אימייל הוקפץ לשיווק במקום
      // לאפליקציה. "/" משאיר את הסיום בתוך ה-SPA וה-guards מנתבים משם.
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      <ClerkGatedApp />
    </ClerkProvider>
  );
}

export default function App() {
  return (
    <LocalizationLanguageProvider>
      <ClerkAppShell />
    </LocalizationLanguageProvider>
  );
}
